import { useState, useEffect, useCallback } from "react";
import { Sun, Moon } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { applyTheme, ensureThemeSynchronization, getStoredTheme, getSystemTheme, persistTheme, resolveInitialTheme } from "@/lib/theme";

interface Props {
  isTransparent: boolean;
  borderColor: string;
  textColor?: string;
}

const ThemeToggle = ({ isTransparent, borderColor, textColor }: Props) => {
  const { t } = useLanguage();

  const [isDark, setIsDark] = useState(() => resolveInitialTheme() === "dark");

  useEffect(() => {
    const nextTheme = isDark ? "dark" : "light";
    applyTheme(nextTheme);
    persistTheme(nextTheme);
    ensureThemeSynchronization();
  }, [isDark]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      if (!getStoredTheme()) {
        setIsDark((e.matches ? getSystemTheme() : "light") === "dark");
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggle = useCallback(() => setIsDark((v) => !v), []);

  return (
    <button
      onClick={toggle}
      className="liquid-capsule flex h-8 w-8 items-center justify-center rounded-full border border-transparent transition-all duration-token-base"
      style={{
        color: textColor ?? (isTransparent ? "hsl(var(--foreground) / 0.94)" : "hsl(var(--foreground) / 0.90)"),
        borderColor,
      }}
      aria-label={isDark ? t.theme.light : t.theme.dark}
      title={isDark ? t.theme.light : t.theme.dark}
    >
      {isDark ? (
        <Sun className="h-3.5 w-3.5" />
      ) : (
        <Moon className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

export default ThemeToggle;
